// The move the opponent plays when the thinking never came back.
//
// Deliberately not a smaller version of `planTurn`: this runs when the planner
// has been killed for failing to answer, so it must not be able to fail the
// same way. No cloned positions, no reply search, no industry — one pass over
// the legal moves, taking the best capture on offer and otherwise the first
// move in board order.
//
// Deterministic, like everything else here. A game that fell back to this and
// then replayed would otherwise diverge at exactly the point it was already
// having trouble.

import {
  BISHOP,
  KING,
  KNIGHT,
  PAWN,
  type PieceType,
  pieceType,
  QUEEN,
  ROOK,
  type Side
} from "src/engine/rules/board";
import { moveRefusal, openMoves } from "src/engine/rules/clock";
import { legalMoves } from "src/engine/rules/chess/moveGen";
import { streamFor } from "src/engine/rules/random";
import type { Command, GameState } from "src/engine/rules/commands";
import { moveCapturedType,
  moveTarget} from "src/engine/rules/move";
import { pieceAt } from "src/engine/rules/position";

const VALUE: Record<PieceType, number> = {
  [PAWN]: 100,
  [KNIGHT]: 300,
  [BISHOP]: 300,
  [ROOK]: 500,
  [QUEEN]: 900,
  [KING]: 0
};

/**
 * One legal move for `side`, or an empty plan when it has none.
 *
 * Returns a plan rather than a move so it is a drop-in for `planTurn` — the
 * caller dispatches a list either way and never has to know which produced it.
 */
/**
 * Any legal move at all, chosen at random. The last resort.
 *
 * Reached when three plans in a row have failed, which means something about
 * this position is systematically breaking the planner rather than one worker
 * having had a bad turn. `naiveTurn` is already the safety net; this is the net
 * under the net, and it is deliberately dumber in every way — no scoring, no
 * reply search, no industry commands, nothing that could fail the way the
 * thing it is covering for just did three times.
 *
 * **Seeded on the *attempt*, which is what makes it both things at once.**
 * This used `Math.random`, and the argument for it was real: a stream mixed
 * from the seed and the ply is a pure function of the position, so retrying
 * picks the same move — and the same move is quite possibly what has been
 * breaking. You cannot escape a loop by taking the same step again.
 *
 * Mixing the failure count into the salt answers that without giving up
 * replay. Attempt three rolls a different stream from attempt two, so each
 * retry genuinely takes a different step; and two runs that fail the same way
 * at the same ply play the same move, which `Math.random` could never promise.
 * `streamFor` is the only sanctioned source of randomness in `src/engine/ai/` and
 * there is now no exception to it.
 *
 * **What this does not fix, and cannot:** *how many* times the worker failed is
 * a fact about the machine, not about the game. A slow client can still reach
 * here when a fast one does not, and then the two games differ — but that
 * divergence is the missed deadline, not the move, and no seeding can reach it.
 */
const LOOSE_STREAM = 9000;

/** End Turn (§31 T2): every fallback turn ends itself, or the clock never leaves. */
const END_TURN: Command = { type: "pass" };

export function looseTurn(state: GameState, side: Side, attempt = 0): Command[] {
  if (moveRefusal(state)) return [END_TURN];
  const moves = openMoves(state);
  // No legal move is the game's to settle, not a turn to end; pieces that
  // have all moved (§33 T1) are a turn to end.
  if (moves.length === 0) return legalMoves(state.position).length === 0 ? [] : [END_TURN];
  // Its own salt, far from every other one in `src/engine/ai/` (`side`, `PATROL_SALT`,
  // `OFFER_STREAM`), so the attempt offset cannot walk into a
  // stream some other decision is already using at this ply.
  const roll = streamFor(state.seed, state.position.ply, LOOSE_STREAM + attempt);
  const move = moves[Math.floor(roll() * moves.length)] ?? moves[0];
  // Loudly, because this should never happen and a silent recovery is how a
  // systematic failure goes unnoticed for a month.
  console.warn(
    `[opponent] three plans failed in a row for side ${side}; playing a random ` +
      `legal move to keep the game going. This is a bug — the position is on ` +
      `the board and the planner could not answer it.`
  );
  return [{ type: "move", from: move.from, to: moveTarget(move) }, END_TURN];
}

export function naiveTurn(state: GameState, side: Side): Command[] {
  if (moveRefusal(state)) return [END_TURN];
  const moves = openMoves(state);
  // No legal move is the game's to settle, not a turn to end; pieces that
  // have all moved (§33 T1) are a turn to end.
  if (moves.length === 0) return legalMoves(state.position).length === 0 ? [] : [END_TURN];

  let best = moves[0];
  let bestScore = -Infinity;
  for (const move of moves) {
    const captured = moveCapturedType(move);
    // Take the most valuable thing available, and value it net of what it can
    // take back for free — a queen for a pawn is not a capture worth making
    // even when there is nothing else to do.
    const gain = captured ? VALUE[captured] : 0;
    const risk = VALUE[pieceType(pieceAt(state.position, move.from))] / 10;
    const score = gain - risk;
    // Strictly greater, so ties keep board order and the choice stays fixed.
    if (score > bestScore) {
      bestScore = score;
      best = move;
    }
  }

  void side;
  return [{ type: "move", from: best.from, to: moveTarget(best) }, END_TURN];
}
